import { create } from "zustand";
import { createJSONStorage, persist, StateStorage } from "zustand/middleware";
import { OpusAgentModel } from "@src/types.generated";
import { isOpusAgentModelAvailable, OPUS_AGENT_MODEL_PRESETS } from "./models";

// The admin's last explicitly picked Opus Agent model, persisted per the
// localStorage rule (zustand + persist, in-memory state as the truth). It
// seeds the selection for NEW chats only — an existing chat's stored model
// (server truth) always wins. Device preference, not signed-in-user data,
// so logout.tsx deliberately does not clear it. A model no longer offered
// in the picker is no preference at all, so it never seeds a new chat.
export const sanitizeStoredModel = (value: unknown): OpusAgentModel | null =>
  typeof value === "string" &&
  (Object.values(OpusAgentModel) as string[]).includes(value) &&
  isOpusAgentModelAvailable(value as OpusAgentModel)
    ? (value as OpusAgentModel)
    : null;

// Version 1 stored the model a preset resolved to under the GPT-5.6 mapping;
// carry each over to what that preset resolves to now.
const V1_PRESET_MODELS: Partial<Record<OpusAgentModel, OpusAgentModel>> = {
  [OpusAgentModel.Gpt_5_6Terra]: OPUS_AGENT_MODEL_PRESETS.auto,
  [OpusAgentModel.Gpt_5_6Luna]: OPUS_AGENT_MODEL_PRESETS.auto,
  [OpusAgentModel.Gpt_5_6Sol]: OPUS_AGENT_MODEL_PRESETS.power,
};

// Version 2 stored Auto and Fast as GPT-6 Sol and GPT-6 Luna. Fast is gone;
// both carry over to Auto (Claude Sonnet 5.5).
const V2_PRESET_MODELS: Partial<Record<OpusAgentModel, OpusAgentModel>> = {
  [OpusAgentModel.Gpt_6Sol]: OPUS_AGENT_MODEL_PRESETS.auto,
  [OpusAgentModel.Gpt_6Luna]: OPUS_AGENT_MODEL_PRESETS.auto,
};

type ModelPreferenceState = {
  preferredModel: OpusAgentModel | null;
  setPreferredModel: (model: OpusAgentModel) => void;
};

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

const safeLocalStorage: StateStorage = {
  getItem: (name) => {
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      window.localStorage.setItem(name, value);
    } catch {
      return;
    }
  },
  removeItem: (name) => {
    try {
      window.localStorage.removeItem(name);
    } catch {
      return;
    }
  },
};

export const useOpusAgentModelPreferenceStore = create<ModelPreferenceState>()(
  persist(
    (set) => ({
      preferredModel: null,
      setPreferredModel: (model) =>
        set({ preferredModel: sanitizeStoredModel(model) }),
    }),
    {
      name: "opus-agent-model-preference",
      version: 3,
      migrate: (persisted, version) => {
        let preferredModel = (persisted as { preferredModel?: unknown } | null)
          ?.preferredModel;
        if (typeof preferredModel !== "string") return persisted;
        if (version < 2) {
          preferredModel =
            V1_PRESET_MODELS[preferredModel as OpusAgentModel] ??
            preferredModel;
        }
        if (version < 3) {
          preferredModel =
            V2_PRESET_MODELS[preferredModel as OpusAgentModel] ??
            preferredModel;
        }
        return { preferredModel };
      },
      storage: createJSONStorage(() =>
        typeof window === "undefined" ? noopStorage : safeLocalStorage,
      ),
      partialize: (state) => ({ preferredModel: state.preferredModel }),
      merge: (persisted, current) => ({
        ...current,
        // An invalid or since-removed enum value falls back to no preference.
        preferredModel: sanitizeStoredModel(
          (persisted as { preferredModel?: unknown } | null)?.preferredModel,
        ),
      }),
    },
  ),
);
